import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { JVInput } from "../../../../../shared/JVInput";
import { Button } from "primereact/button";
import { IBranchMastDropdown, ICompanyMastDropdown, IUsersDropdown } from "../../../../../models/IDropdown";
import { Fetch } from "../../../../../shared/Fetch";
import { MultiSelect } from "primereact/multiselect";
import { ILogin } from "../../../../../models/Login";
import { DataTable } from "primereact/datatable";
import { Column, ColumnEditorOptions } from "primereact/column";
import { safeFormatDate } from "../../../../../models/DateFormatMapper";

interface Props {
  reload: boolean;
  setReload: (reload: boolean) => void;
  toast: any;
}

interface DiceUserSettingFormValues {
  No_Of_Views: number | string;
}

interface IDiceUserSetting {
  id?: number;
  login_ID: number;
  user_Name?: string;
  company_Name?: string;
  branch_Name?: string;
  no_Of_Views: number;
  updated_By?: number;
  updated_At?: string;
}

const schema = yup.object({
  No_Of_Views: yup.number().typeError("Must be a number").positive("Must be greater than 0").integer("Must be a whole number").required("Number of Views Allowed is required"),
});

const DiceUserSettingForm = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [tableSaveLoading, setTableSaveLoading] = useState<boolean>(false);
  const [companyList, setCompanyList] = useState<Array<ICompanyMastDropdown>>([]);
  const [branchList, setBranchList] = useState<Array<IBranchMastDropdown>>([]);
  const [userList, setUserList] = useState<Array<IUsersDropdown>>([]);
  const [selectedCompany, setSelectedCompany] = useState<Array<number>>([]);
  const [selectedBranch, setSelectedBranch] = useState<Array<number>>([]);
  const [selectedUsers, setSelectedUsers] = useState<Array<number>>([]);
  const [tableData, setTableData] = useState<Array<IDiceUserSetting>>([]);
  const [focusIndex, setFocusIndex] = useState<number>(-1);
  const dt = useRef<any>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<DiceUserSettingFormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: { No_Of_Views: "" },
    shouldFocusError: false,
  });

  const _roles: Array<number> = [];

  const loginObj: ILogin = JSON.parse(sessionStorage.getItem("auth") + "");

  loginObj.roles.forEach((role) => _roles.push(role.role_ID));

  const DateFormat = loginObj.date_Format;

  const isAdminOrManager = _roles.includes(1) || _roles.includes(26);

  const loadCompanies = async () => {
    const response = await Fetch("Dice_Setting/getCompany", "GET", null, null, null);
    if (response) {
      const data: Array<ICompanyMastDropdown> = await response.json();
      data.unshift({ company_ID: 0, company_Name: "-- SELECT --" });
      setCompanyList(data);
    }
  };

  const loadBranches = async (company: number[]) => {
    const queryParam = company.length > 0 ? `company_id=${company.join(",")}` : null;
    const response = await Fetch("Dice_Setting/getBranchsMulti", "GET", null, queryParam, null);
    if (response) {
      const data: Array<IBranchMastDropdown> = await response.json();
      data.unshift({ branch_ID: 0, branchName: "-- SELECT --", company_ID: 0 });
      setBranchList(data);
      setSelectedBranch([]);
      setSelectedUsers([]);
    }
  };

  const loadUsers = async (branch: number[], company: number[]) => {
    let queryParameter = null;
    if (branch.length > 0) queryParameter = `branch_IDs=${branch}`;
    else if (company.length > 0) queryParameter = `company_IDs=${company}`;
    const response = await Fetch("Dice_Setting/getUsers", "GET", null, queryParameter, null);
    if (response) {
      const data: Array<IUsersDropdown> = await response.json();
      data.unshift({ login_ID: 0, user_Name: "-- SELECT --" });
      setUserList(data);
      setSelectedUsers([]);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany.length > 0) loadBranches(selectedCompany);
    else {
      setBranchList([]);
      setSelectedBranch([]);
      setUserList([]);
      setSelectedUsers([]);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedCompany.length > 0 || selectedBranch.length > 0) loadUsers(selectedBranch, selectedCompany);
    else {
      setUserList([]);
      setSelectedUsers([]);
    }
  }, [selectedBranch, selectedCompany]);

  const loadTableData = async () => {
    setTableLoading(true);
    const response = await Fetch("Dice_Setting/getDiceUserSetting", "GET", null, null, props.toast);
    if (response) {
      const data: Array<IDiceUserSetting> = await response.json();
      setTableData(data);
    }
    setTableLoading(false);
  };

  useEffect(() => {
    loadTableData();
  }, [props.reload]);

  const onSubmit = async (data: DiceUserSettingFormValues) => {
    if (selectedUsers.length === 0) {
      props.toast?.current?.show({ severity: "warn", detail: "Please select at least one user.", life: 3000 });
      return;
    }

    try {
      setLoading(true);
      const requests = selectedUsers.map((userId: number) =>
        Fetch(
          "Dice_Setting/addDiceUserSetting",
          "POST",
          {
            User_ID: userId,
            No_Of_Views: data.No_Of_Views,
          },
          null,
          props.toast,
        ),
      );

      const results = await Promise.all(requests);
      const allSuccess = results.every((r) => r);

      if (allSuccess) {
        props.toast?.current?.show({ severity: "success", detail: "Added Successfully", life: 3000 });
        props.setReload(!props.reload);
        reset();
        setSelectedCompany([]);
        setSelectedBranch([]);
        setSelectedUsers([]);
      }
    } catch {
      props.toast?.current?.show({ severity: "error", detail: "Save failed", life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const onTableSave = async () => {
    setTableSaveLoading(true);
    const payload = tableData.map((el) => ({
      id: el.id,
      login_ID: el.login_ID,
      no_Of_Views: el.no_Of_Views,
    }));
    const resp = await Fetch("Dice_Setting/updateDiceUserSetting", "PUT", payload, null, props.toast);
    if (resp) {
      props.toast?.current?.show({ severity: "success", detail: "Updated Successfully", life: 3000 });
      props.setReload(!props.reload);
      setFocusIndex(-1);
    }
    setTableSaveLoading(false);
  };

  const isPositiveInteger = (val: any) => {
    const n = Number(val);
    return Number.isInteger(n) && n > 0;
  };

  const onCellEditComplete = (e: any) => {
    const { rowData, newValue, field, originalEvent: event } = e;
    if (field === "no_Of_Views") {
      if (isPositiveInteger(newValue)) rowData[field] = newValue;
      else event.preventDefault();
    } else {
      if (newValue?.toString().trim().length > 0) rowData[field] = newValue;
      else event.preventDefault();
    }
    setTableData((prev) =>
      prev.map((item, index) => {
        if (index === focusIndex) setFocusIndex(-1);
        if (item.id === rowData.id) {
          return {
            ...item,
            no_Of_Views: isPositiveInteger(rowData.no_Of_Views) ? Number(rowData.no_Of_Views) : item.no_Of_Views,
          };
        }
        return item;
      }),
    );
  };

  const cellEditor = (options: ColumnEditorOptions) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (options.editorCallback) options.editorCallback(e.target.value);
    };
    return <input style={{ width: "100%", margin: 0, padding: "3px 5px", border: "2px solid green", cursor: "pointer" }} value={options.value} type="number" onChange={handleChange} />;
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-fluid formgrid grid align-items-end">
          {isAdminOrManager && (
            <div className="field col-2 md:col-2">
              <p>Company</p>
              <MultiSelect style={{ height: "37px", marginTop: "-10px" }} value={selectedCompany} onChange={(e) => setSelectedCompany(e.value)} options={companyList} optionValue="company_ID" optionLabel="company_Name" display="chip" filter placeholder="Select Company" />
            </div>
          )}
          {isAdminOrManager && (
            <div className="field col-2 md:col-2">
              <p>Branch</p>
              <MultiSelect
                style={{ height: "37px", marginTop: "-10px" }}
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.value)}
                options={branchList}
                optionValue="branch_ID"
                optionLabel="branchName"
                display="chip"
                filter
                placeholder="Select Branches"
                disabled={selectedCompany.length === 0}
              />
            </div>
          )}
          {isAdminOrManager && (
            <div className="field col-2 md:col-2">
              <p>User</p>
              <MultiSelect style={{ height: "37px", marginTop: "-10px" }} value={selectedUsers} onChange={(e) => setSelectedUsers(e.value)} options={userList} optionValue="login_ID" optionLabel="user_Name" display="chip" filter placeholder="Select User" disabled={selectedCompany.length === 0} />
            </div>
          )}

          <JVInput required type="number" column={4} label="Number of Views Allowed" control={control} name="No_Of_Views" error={errors.No_Of_Views?.message} />

          <div className="field col-2 md:col-2 flex align-items-end pb-2">
            <Button loading={loading} disabled={loading} style={{ width: "100px", height: "37px" }} label="ADD" type="submit" />
          </div>
        </div>
      </form>

      <DataTable style={{ border: "1px solid rgba(105,105,105,0.17)", borderTopWidth: "0", marginTop: "1rem" }} size="small" ref={dt} value={tableData} emptyMessage="No Data found." loading={tableLoading} showGridlines stripedRows responsiveLayout="scroll">
        <Column field="company_Name" header="Company" />
        <Column field="branch_Name" header="Branch" />
        <Column field="teamName" header="Team Name" />
        <Column field="user_Name" header="User" />
        <Column
          field="no_Of_Views"
          header="No of Views Allowed"
          body={(rowData: IDiceUserSetting, options: any) => (
            <p className={options.rowIndex === focusIndex ? "glow-loop" : ""} style={{ color: options.rowIndex === focusIndex ? "red" : "black" }}>
              {rowData.no_Of_Views}
            </p>
          )}
          editor={(options) => cellEditor(options)}
          onCellEditComplete={onCellEditComplete}
        />
        <Column header="Created At" body={(rowData: any) => safeFormatDate(rowData.created_At, "dd/MM/yyyy", DateFormat, true)} />
        <Column field="createdBy" header="Created By" />
        <Column field="updatedBy" header="Updated By" />
      </DataTable>

      <div className="col-12 md:col-12 flex gap-4 justify-content-end mt-2">
        <Button loading={tableSaveLoading} disabled={tableSaveLoading || tableData.length === 0} style={{ width: "100px" }} label="SAVE" type="button" onClick={onTableSave} />
      </div>
    </>
  );
};

export default DiceUserSettingForm;
